import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const PaperView = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [paper, setPaper] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Extract navigation state
  const navigationState = location.state || {};
  const referrerPath = navigationState.from;
  const referrerLabel = navigationState.fromLabel;

  useEffect(() => {
    fetchPaper();
  }, [id]);

  const fetchPaper = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/papers/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setPaper(response.data);
    } catch (err) {
      console.error('Error fetching paper:', err);
      setError('Failed to load paper details. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const downloadManuscript = async (paperId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/papers/${paperId}/download`, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        responseType: 'blob'
      });

      // Create a download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `manuscript_${paperId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading manuscript:', err);
      alert('Failed to download manuscript. Please try again later.');
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'SUBMITTED':
        return 'bg-info';
      case 'UNDER_REVIEW':
        return 'bg-primary';
      case 'ACCEPTED':
        return 'bg-success';
      case 'REJECTED':
        return 'bg-danger';
      case 'REVISIONS_REQUIRED':
        return 'bg-warning';
      case 'WITHDRAWN':
        return 'bg-secondary';
      default:
        return 'bg-light text-dark';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Determine appropriate back navigation
  const getBackNavigation = () => {
    // If we have a referrer from navigation state, use it
    if (referrerPath && referrerLabel) {
      return {
        path: referrerPath,
        label: `Back to ${referrerLabel}`
      };
    }

    // Fallback based on authentication status
    if (user) {
      return {
        path: '/dashboard',
        label: 'Back to Dashboard'
      };
    } else {
      return {
        path: '/',
        label: 'Back to Home'
      };
    }
  };

  const backNavigation = getBackNavigation();

  if (loading) {
    return (
      <div className="container py-5">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container py-5">
        <div className="alert alert-danger">
          {error}
        </div>
        <Link to={backNavigation.path} className="btn btn-outline-secondary">
          <i className="fas fa-arrow-left me-2"></i>{backNavigation.label}
        </Link>
      </div>
    );
  }

  if (!paper) {
    return (
      <div className="container py-5">
        <div className="alert alert-warning">
          Paper not found.
        </div>
        <Link to={backNavigation.path} className="btn btn-outline-secondary">
          <i className="fas fa-arrow-left me-2"></i>{backNavigation.label}
        </Link>
      </div>
    );
  }

  return (
    <div className="container py-5">
      <div className="row mb-4">
        <div className="col">
          <Link to={backNavigation.path} className="btn btn-outline-secondary mb-3">
            <i className="fas fa-arrow-left me-2"></i>{backNavigation.label}
          </Link>
        </div>
      </div>

      <div className="card shadow-sm">
        <div className="card-header bg-primary text-white">
          <h2 className="mb-0">Paper Details</h2>
        </div>
        <div className="card-body">
          {/* Title and Status */}
          <div className="row mb-4">
            <div className="col">
              <h3>{paper.title}</h3>
              <span className={`badge ${getStatusBadgeClass(paper.status)} me-2`}>
                {paper.status?.replace(/_/g, ' ')}
              </span>
              <span className="text-muted">
                Submitted on {formatDate(paper.submittedDate || paper.submittedAt)}
              </span>
              {paper.journalIssue && paper.journalIssue.publicationDate && (
                <div className="text-muted mt-1 small">
                  Published in {paper.journalIssue.title} on {formatDate(paper.journalIssue.publicationDate)}
                </div>
              )}
            </div>
          </div>

          {/* Abstract */}
          <div className="mb-4">
            <h5>Abstract</h5>
            <p className="text-justify">{paper.abstract}</p>
          </div>

          {/* Keywords */}
          <div className="mb-4">
            <h5>Keywords</h5>
            <p>{paper.keywords}</p>
          </div>

          {/* Authors */}
          <div className="mb-4">
            <h5>Authors</h5>
            {paper.authors && typeof paper.authors === 'string' ? (
              <p>{paper.authors}</p>
            ) : Array.isArray(paper.authors) ? (
              <ul>
                {paper.authors.map((author, index) => (
                  <li key={index}>
                    {typeof author === 'object' ?
                      `${author.name}${author.affiliation ? ` - ${author.affiliation}` : ''} ${author.isPrimaryContact ? '(Primary Contact)' : ''}` :
                      author
                    }
                  </li>
                ))}
              </ul>
            ) : (
              <p>No authors listed</p>
            )}
          </div>

          {/* Additional Information */}
          <div className="row">
            <div className="col-md-6 mb-3">
              <h5>Category</h5>
              <p>{paper.category || 'N/A'}</p>
            </div>
            <div className="col-md-6 mb-3">
              <h5>Funding Information</h5>
              <p>{paper.fundingInfo || 'N/A'}</p>
            </div>
          </div>

          {/* Cover Letter */}
          {paper.coverLetter && (
            <div className="mb-4">
              <h5>Cover Letter</h5>
              <div className="border rounded p-3 bg-light">
                <p style={{ whiteSpace: 'pre-wrap' }}>{paper.coverLetter}</p>
              </div>
            </div>
          )}

          {/* Ethics Statement */}
          {paper.ethicsStatement && (
            <div className="mb-4">
              <h5>Ethics Statement</h5>
              <p>{paper.ethicsStatement}</p>
            </div>
          )}

          {/* Conflict of Interest */}
          {paper.conflictOfInterest && (
            <div className="mb-4">
              <h5>Conflict of Interest</h5>
              <p>{paper.conflictOfInterest}</p>
            </div>
          )}

          {/* Acknowledgments */}
          {paper.acknowledgments && (
            <div className="mb-4">
              <h5>Acknowledgments</h5>
              <p>{paper.acknowledgments}</p>
            </div>
          )}

          {/* Actions */}
          <div className="mt-4 pt-4 border-top">
            <div className="d-flex gap-2">
              {paper.status === 'REVISIONS_REQUIRED' && (
                <Link
                  to={`/submit-paper?revision=${paper.id}`}
                  className="btn btn-warning"
                >
                  <i className="fas fa-edit me-2"></i>Submit Revision
                </Link>
              )}
              {paper.manuscriptPath && (
                <button
                  onClick={() => downloadManuscript(paper.id)}
                  className="btn btn-outline-primary"
                >
                  <i className="fas fa-download me-2"></i>Download Manuscript
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Review Comments (if any) */}
      {paper.reviewComments && paper.reviewComments.length > 0 && (
        <div className="card mt-4 shadow-sm">
          <div className="card-header bg-secondary text-white">
            <h4 className="mb-0">Review Comments</h4>
          </div>
          <div className="card-body">
            {paper.reviewComments.map((comment, index) => (
              <div key={index} className="mb-3 pb-3 border-bottom">
                <div className="d-flex justify-content-between">
                  <strong>{comment.reviewer || 'Reviewer'}</strong>
                  <span className="text-muted">{formatDate(comment.date)}</span>
                </div>
                <p className="mt-2">{comment.text}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default PaperView;